import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ApiRequestError,
  fetchJoinedTeamMembersPage,
  fetchMemberWorkStatistics,
  type MyStatisticsOrder,
} from '@/api';
import { DateRangeCalendar } from '@/components/ui/DateRangeCalendar';
import { OptionSelect, type OptionSelectItem } from '@/components/ui/OptionSelect';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

const PAGE_SIZE = 20;

type DateRange = {
  startDate: string;
  endDate: string;
};

type Summary = {
  totalHours: number;
  totalCommission: number;
  orderCount: number;
};

function thisMonthRange(): DateRange {
  const today = dayjs();
  return {
    startDate: today.startOf('month').format('YYYY-MM-DD'),
    endDate: today.format('YYYY-MM-DD'),
  };
}

function last7DaysRange(): DateRange {
  const today = dayjs();
  return {
    startDate: today.subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: today.format('YYYY-MM-DD'),
  };
}

function formatRangeDay(value: string): string {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  return parsed.format('D MMM YYYY');
}

function formatRangeLabel(range: DateRange): string {
  if (!range.startDate && !range.endDate) return 'All dates';
  if (range.startDate && range.endDate) {
    if (range.startDate === range.endDate) return formatRangeDay(range.startDate);
    return `${formatRangeDay(range.startDate)} – ${formatRangeDay(range.endDate)}`;
  }
  return formatRangeDay(range.startDate || range.endDate);
}

function pendingRangeLabel(range: DateRange): string {
  if (!range.startDate) return 'Select a day, then confirm.';
  if (!range.endDate || range.startDate === range.endDate) {
    return `One day: ${formatRangeDay(range.startDate)}`;
  }
  return `${formatRangeDay(range.startDate)} – ${formatRangeDay(range.endDate)}`;
}

function formatServiceDay(value: string): string {
  if (!value.trim()) return '—';
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  const hasTime = /[T\s]\d{1,2}:\d{2}/.test(value) && !(parsed.hour() === 0 && parsed.minute() === 0 && parsed.second() === 0);
  return hasTime ? parsed.format('D MMM YYYY HH:mm') : parsed.format('D MMM YYYY');
}

function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours <= 0) return '0 h';
  const text = hours.toFixed(10).replace(/\.?0+$/, '');
  return `${text} h`;
}

function formatMoney(amount: number | null): string {
  if (amount == null || !Number.isFinite(amount)) return '—';
  return `$${amount.toFixed(2)}`;
}

function rangesEqual(a: DateRange, b: DateRange): boolean {
  return a.startDate === b.startDate && a.endDate === b.endDate;
}

function groupByTeam(items: MyStatisticsOrder[]): Array<{ teamName: string; orders: MyStatisticsOrder[] }> {
  const groups: Array<{ teamName: string; orders: MyStatisticsOrder[] }> = [];
  const indexByTeam = new Map<string, number>();
  items.forEach((item) => {
    const existing = indexByTeam.get(item.teamName);
    if (existing === undefined) {
      indexByTeam.set(item.teamName, groups.length);
      groups.push({ teamName: item.teamName, orders: [item] });
      return;
    }
    groups[existing]?.orders.push(item);
  });
  return groups;
}

export default function TeamStatisticsPage() {
  const navigate = useNavigate();
  const [appliedRange, setAppliedRange] = useState<DateRange>(thisMonthRange);
  const [draftRange, setDraftRange] = useState<DateRange>(thisMonthRange);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [staffOptions, setStaffOptions] = useState<OptionSelectItem[]>([
    { value: '', label: 'All staff' },
  ]);
  const [staffError, setStaffError] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<MyStatisticsOrder[]>([]);
  const [summary, setSummary] = useState<Summary>({ totalHours: 0, totalCommission: 0, orderCount: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    fetchJoinedTeamMembersPage({ page: 1, pageSize: 200 })
      .then(({ items: members }) => {
        if (cancelled) return;
        const seen = new Set<string>();
        const options: OptionSelectItem[] = [{ value: '', label: 'All staff' }];
        members
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .forEach((member) => {
            const id = String(member.operatorId).trim();
            if (!id || seen.has(id)) return;
            seen.add(id);
            options.push({ value: id, label: member.name.trim() || 'Staff' });
          });
        setStaffOptions(options);
      })
      .catch(() => {
        if (!cancelled) setStaffError('Could not load the staff list.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (page === 1) {
      setLoading(true);
      setItems([]);
    } else {
      setLoadingMore(true);
    }
    setError('');

    const operatorId = Number(staffId);
    fetchMemberWorkStatistics({
      startDate: appliedRange.startDate,
      endDate: appliedRange.endDate,
      page,
      pageSize: PAGE_SIZE,
      operatorId: staffId && Number.isInteger(operatorId) ? operatorId : undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setItems((prev) => (page === 1 ? result.items : [...prev, ...result.items]));
        if (page === 1) {
          setSummary({
            totalHours: result.totalHours,
            totalCommission: result.totalCommission,
            orderCount: result.orderCount,
          });
          setTotal(result.total);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (page === 1) {
          setItems([]);
          setSummary({ totalHours: 0, totalCommission: 0, orderCount: 0 });
          setTotal(0);
        }
        setError(e instanceof ApiRequestError ? e.message : 'Failed to load work statistics.');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appliedRange.endDate, appliedRange.startDate, page, staffId]);

  const groups = useMemo(() => groupByTeam(items), [items]);
  const hasMore = items.length > 0 && items.length < total;
  const monthRange = thisMonthRange();
  const weekRange = last7DaysRange();
  const allRange: DateRange = { startDate: '', endDate: '' };

  const applyRange = (next: DateRange) => {
    setDraftRange(next);
    setCalendarOpen(false);
    setPage(1);
    setAppliedRange(next);
  };

  const toggleCalendar = () => {
    setCalendarOpen((open) => {
      setDraftRange(appliedRange);
      return !open;
    });
  };

  const cancelCalendar = () => {
    setDraftRange(appliedRange);
    setCalendarOpen(false);
  };

  const confirmCalendar = () => {
    if (!draftRange.startDate) return;
    const endDate = draftRange.endDate || draftRange.startDate;
    applyRange({ startDate: draftRange.startDate, endDate });
  };

  const handleStaffChange = (nextStaffId: string) => {
    setStaffId(nextStaffId);
    setPage(1);
  };

  const handleDraftChange = (next: DateRange) => {
    setDraftRange(next);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="Team Work Statistics"
        subtitle="Staff hours and commission"
        onBack={() => navigate('/me')}
      />

      <div className="px-4 pt-3 pb-[max(1.5rem,env(safe-area-inset-bottom))] space-y-2.5">
        <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
          <div>
            <div className="text-[11px] text-gray-500 mb-1">Staff</div>
            <OptionSelect
              id="team-statistics-staff"
              value={staffId}
              options={staffOptions}
              onChange={handleStaffChange}
            />
            {staffError ? <p className="text-[11px] text-red-500 mt-1">{staffError}</p> : null}
          </div>

          <div className="flex gap-1.5">
            {([
              { id: 'month', label: 'This month', range: monthRange },
              { id: 'week', label: 'Last 7 days', range: weekRange },
              { id: 'all', label: 'All', range: allRange },
            ] as const).map((preset) => {
              const active = rangesEqual(appliedRange, preset.range);
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyRange(preset.range)}
                  className={`flex-1 h-8 rounded-md text-[11px] font-medium cursor-pointer border ${
                    active
                      ? 'bg-green-50 border-green-500 text-green-700'
                      : 'bg-white border-gray-200 text-gray-500'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={toggleCalendar}
            className="w-full h-9 px-2.5 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-between cursor-pointer"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <i className="ri-calendar-line text-gray-400 text-sm" />
              <span className="text-xs text-gray-700 truncate">{formatRangeLabel(appliedRange)}</span>
            </span>
            <i className={`ri-arrow-down-s-line text-gray-400 text-base transition-transform ${calendarOpen ? 'rotate-180' : ''}`} />
          </button>

          {calendarOpen ? (
            <div className="space-y-2.5">
              <DateRangeCalendar
                startDate={draftRange.startDate}
                endDate={draftRange.endDate}
                onChange={handleDraftChange}
              />
              <p className="text-[11px] text-gray-400 leading-relaxed">{pendingRangeLabel(draftRange)}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={cancelCalendar}
                  className="flex-1 h-8 rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-600 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmCalendar}
                  disabled={!draftRange.startDate}
                  className="flex-1 h-8 rounded-md bg-green-500 hover:bg-green-600 text-white text-xs font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Confirm
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
            <div className="text-[10px] text-gray-400 uppercase tracking-wider">Total hours</div>
            <div className="mt-1 text-lg font-semibold text-gray-800 tabular-nums">
              {loading ? '—' : formatHours(summary.totalHours)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
            <div className="text-[10px] text-gray-400 uppercase tracking-wider">Total commission</div>
            <div className="mt-1 text-lg font-semibold text-green-600 tabular-nums">
              {loading ? '—' : formatMoney(summary.totalCommission)}
            </div>
          </div>
        </div>

        <div className="text-[11px] text-gray-400 px-0.5">
          {loading ? 'Loading orders…' : `${summary.orderCount} order${summary.orderCount === 1 ? '' : 's'}`}
        </div>

        {error ? (
          <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <i className="ri-loader-4-line text-2xl text-green-500 animate-spin" />
            <p className="text-gray-400 text-xs mt-2">Loading statistics…</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <i className="ri-bar-chart-box-line text-3xl text-gray-300" />
            <p className="text-sm text-gray-400 font-medium mt-2">No work in this period</p>
            <p className="text-xs text-gray-300 mt-1">Completed orders for the selected staff will show hours and commission here.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {groups.map((group) => (
              <div key={group.teamName} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5">
                  <i className="ri-team-line text-gray-400 text-sm" />
                  <span className="text-xs font-semibold text-gray-800 truncate">{group.teamName}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {group.orders.map((order) => (
                    <div key={order.id} className="px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <i className="ri-map-pin-2-line text-gray-300 text-sm mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-800 leading-snug">{order.address}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-gray-400">
                            <span>{formatServiceDay(order.serviceDate)}</span>
                            <span className="text-gray-300">·</span>
                            <span className="tabular-nums">{formatHours(order.hours)}</span>
                            {order.statusText ? (
                              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-500">
                                {order.statusText}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div className="text-xs font-semibold text-green-600 tabular-nums flex-shrink-0">
                          {formatMoney(order.commission)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {hasMore ? (
              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={loadingMore}
                className="w-full h-9 rounded-lg border border-gray-200 bg-white text-xs text-gray-600 cursor-pointer disabled:opacity-60"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
